/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { V3DialogService } from '@app/app/shared/helpers/dialog/v3-dialog.service';
import { TOAST_NOTIFICATION_SERVICE, ToastService } from '@app/app/shared/services/toast-notifications/toast-service';
import { PtlV3DialogShellComponent } from '@app/app/shared/styleguide/components/dialog-shell/ptl-v3-dialog-shell.component';
import { StyleguideModule } from '@app/app/shared/styleguide/styleguide.module';
import { LucideSearch, LucideTrash2, LucideX } from '@lucide/angular';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map, switchMap, tap } from 'rxjs/operators';

/** One selectable thing — a role, a group — reduced to what the dialog renders. */
export interface V3MembershipItem {
  id: string;
  label: string;
  sublabel?: string;
}

export interface V3MembershipPage {
  items: V3MembershipItem[];
  total: number;
}

/** Resolves one page of candidates; `null` stands for a failed request. */
export type V3MembershipSearch = (page: number, pageSize: number, term: string) => Observable<V3MembershipPage | null>;

export type V3MembershipSubmit = (itemIds: string[], memberIds: string[]) => Observable<{ isSuccess: boolean; error?: string }>;

/**
 * Drives "manage roles" and "manage groups": both search a paginated list, collect a selection, and
 * either add it to or remove it from the checked members. Everything role- or group-specific arrives
 * as this config.
 */
export interface V3EditMembershipDialogData {
  checkedMemberIds: string[];
  titleKey: string;
  descriptionKey: string;
  searchLabelKey: string;
  searchPlaceholderKey: string;
  emptyKey: string;
  addButtonKey: string;
  removeButtonKey: string;
  addSuccessKey: string;
  removeSuccessKey: string;
  loadErrorKey: string;
  search: V3MembershipSearch;
  add: V3MembershipSubmit;
  remove: V3MembershipSubmit;
}

type SubmitAction = 'add' | 'remove';

interface PageRequest {
  term: string;
  page: number;
  append: boolean;
}

const SEARCH_DEBOUNCE_TIME = 500;
const PAGE_SIZE = 10;

@Component({
  selector: 'ptl-v3-edit-membership-dialog',
  templateUrl: './v3-edit-membership-dialog.component.html',
  styleUrls: ['./v3-edit-membership-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TranslocoModule, StyleguideModule, PtlV3DialogShellComponent],
})
export class V3EditMembershipDialogComponent {
  protected readonly LucideSearch = LucideSearch;
  protected readonly LucideX = LucideX;
  protected readonly LucideTrash2 = LucideTrash2;

  protected readonly data: V3EditMembershipDialogData = inject(MAT_DIALOG_DATA);
  protected readonly memberCount = this.data.checkedMemberIds.length;

  protected readonly searchControl = new FormControl('');

  protected readonly loading = signal(true);
  protected readonly loadingMore = signal(false);
  protected readonly canLoadMore = signal(false);
  protected readonly pendingAction = signal<SubmitAction | null>(null);
  protected readonly candidates = signal<V3MembershipItem[]>([]);
  protected readonly selected = signal<V3MembershipItem[]>([]);

  protected readonly selectedIds = computed(() => new Set(this.selected().map((item) => item.id)));
  protected readonly selectedCount = computed(() => this.selected().length);
  protected readonly hasSelection = computed(() => this.selectedCount() > 0);
  protected readonly submitting = computed(() => this.pendingAction() !== null);
  protected readonly showLoading = computed(() => this.loading() && !this.candidates().length);
  protected readonly showEmptyState = computed(() => !this.loading() && !this.candidates().length);

  private readonly dialogRef = inject(MatDialogRef<V3EditMembershipDialogComponent, boolean>);
  private readonly toastService = inject<ToastService>(TOAST_NOTIFICATION_SERVICE);
  private readonly translocoService = inject(TranslocoService);
  private readonly v3DialogService = inject(V3DialogService);

  private readonly pageRequest$ = new Subject<PageRequest>();
  private loadedPage = 0;

  constructor() {
    this.pageRequest$
      .pipe(
        switchMap((request) => this.data.search(request.page, PAGE_SIZE, request.term).pipe(map((page) => ({ request, page })))),
        takeUntilDestroyed(),
      )
      .subscribe(({ request, page }) => this.applyPage(request, page));

    this.searchControl.valueChanges
      .pipe(
        tap(() => this.loading.set(true)),
        debounceTime(SEARCH_DEBOUNCE_TIME),
        takeUntilDestroyed(),
      )
      .subscribe((term) => this.requestFirstPage(term ?? ''));

    this.requestFirstPage('');
  }

  protected toggle(item: V3MembershipItem): void {
    this.selected.update((items) =>
      items.some((selected) => selected.id === item.id) ? items.filter((selected) => selected.id !== item.id) : [...items, item],
    );
  }

  protected remove(item: V3MembershipItem): void {
    this.selected.update((items) => items.filter((selected) => selected.id !== item.id));
  }

  protected onShowMore(): void {
    if (!this.canLoadMore() || this.loadingMore() || this.loading()) {
      return;
    }
    this.loadingMore.set(true);
    this.pageRequest$.next({ term: this.searchControl.value ?? '', page: this.loadedPage + 1, append: true });
  }

  protected async submit(action: SubmitAction): Promise<void> {
    if (!this.hasSelection() || this.submitting()) {
      return;
    }

    if (action === 'remove') {
      const confirmed = await this.v3DialogService.showConfirmDialog({
        title: this.translocoService.translate('translations.dialog.title.confirmRemoveItem'),
        confirmButtonText: this.translocoService.translate('translations.global.button.remove'),
        style: 'destructive',
      });
      if (!confirmed || !this.hasSelection() || this.submitting()) {
        return;
      }
    }

    const itemIds = this.selected().map((item) => item.id);
    const request = action === 'add' ? this.data.add : this.data.remove;
    const successKey = action === 'add' ? this.data.addSuccessKey : this.data.removeSuccessKey;

    this.pendingAction.set(action);
    this.dialogRef.disableClose = true;
    request(itemIds, this.data.checkedMemberIds).subscribe(({ isSuccess, error }) => {
      this.pendingAction.set(null);
      this.dialogRef.disableClose = false;
      if (!isSuccess) {
        this.toastService.showFail(error);
        return;
      }
      this.toastService.showSuccess(this.translocoService.translate(successKey));
      this.dialogRef.close(true);
    });
  }

  protected closeDialog(): void {
    if (!this.submitting()) {
      this.dialogRef.close();
    }
  }

  private requestFirstPage(term: string): void {
    this.pageRequest$.next({ term, page: 0, append: false });
  }

  private applyPage(request: PageRequest, page: V3MembershipPage | null): void {
    if (page) {
      this.loadedPage = request.page;
      const merged = request.append ? [...this.candidates(), ...page.items] : page.items;
      this.candidates.set(merged);
      this.canLoadMore.set(page.total > merged.length);
    } else {
      if (!request.append) {
        this.loadedPage = 0;
        this.candidates.set([]);
        this.canLoadMore.set(false);
      }
      this.toastService.showFail(this.translocoService.translate(this.data.loadErrorKey));
    }
    this.loading.set(false);
    this.loadingMore.set(false);
  }
}
